import http from "./httpService";

const LOGGER_ENDPOINT = "https://logger.mysembra.com";

const originalConsole = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  debug: console.debug.bind(console),
};

let logBuffer = [];
let prevLogTime = "0";
let initialized = false;
let sessionId = generateSessionId();

function generateSessionId() {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${randomPart}`;
}

function stringify(value) {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  try {
    const seen = new WeakSet();
    return JSON.stringify(value, (key, val) => {
      if (typeof val === "object" && val !== null) {
        if (seen.has(val)) return "[Circular Reference]";
        seen.add(val);
      }
      if (val instanceof Error)
        return { name: val.name, message: val.message, stack: val.stack };
      if (typeof val === "function") return "[Function]";
      if (val instanceof HTMLElement) return `[HTMLElement: ${val.tagName}]`;
      return val;
    }, 2);
  } catch {
    return String(value);
  }
}

function sanitizeData(data) {
  if (data === null || data === undefined) return data;
  if (typeof data !== "object") return data;
  try {
    JSON.stringify(data);
    return data;
  } catch {
    return String(data);
  }
}

function addLogEntry(level, message, data, stack) {
  logBuffer.push({
    timestamp: new Date().toISOString(),
    level,
    message,
    data: sanitizeData(data),
    stack,
  });
}

function captureStackTrace() {
  const err = new Error();
  const stack = err.stack || "";
  const lines = stack.split("\n");
  return lines.slice(3).join("\n");
}

function interceptConsoleMethods() {
  const levels = ["log", "info", "warn", "error", "debug"];
  levels.forEach((level) => {
    console[level] = (...args) => {
      originalConsole[level](...args);
      const stack = captureStackTrace();
      const message = args.map((arg) => stringify(arg)).join(" ");
      const errorArg = args.find((arg) => arg instanceof Error);
      addLogEntry(level, message, args.length > 1 ? args : args[0], errorArg?.stack || stack);
    };
  });
}

function setupGlobalErrorHandler() {
  window.addEventListener("error", (event) => {
    addLogEntry(
      "error",
      `Uncaught Error: ${event.message}`,
      { filename: event.filename, lineno: event.lineno, colno: event.colno },
      event.error?.stack
    );
  });
}

function setupUnhandledRejectionHandler() {
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    const stack = reason instanceof Error ? reason.stack : undefined;
    addLogEntry("error", `Unhandled Promise Rejection: ${message}`, reason, stack);
  });
}

function buildReport(error, userDescription) {
  return {
    sessionId,
    userId: getCurrentUserId(),
    metadata: {
      appEnvironment: window.appver ? `production` : "staging",
      appName: "sembra-apply",
      appVersion: window.appver || "unknown",
    },
    userAgent: navigator.userAgent,
    url: window.location.href,
    prevLogTime: prevLogTime || "N/A",
    timestamp: new Date().toISOString(),
    error: {
      message: error?.message || userDescription || "Manual report",
      stack: error?.stack,
      type: error?.name,
    },
    userDescription: userDescription || undefined,
    consoleLogs: [...logBuffer],
  };
}

function getCurrentUserId() {
  try {
    return localStorage.getItem("applicant") || undefined;
  } catch {
    return undefined;
  }
}

async function sendReport(userDescription) {
  const report = buildReport(null, userDescription);
  try {
    await http.post(LOGGER_ENDPOINT + "/logs", report);
    originalConsole.info("[DebugLogging] Bug report sent successfully");
    prevLogTime = report.timestamp;
    logBuffer = [];
    return true;
  } catch (err) {
    originalConsole.error("[DebugLogging] Failed to send bug report:", err);
    return false;
  }
}

function initialize() {
  if (initialized) {
    originalConsole.warn("[DebugLogging] Already initialized");
    return;
  }

  http.get(LOGGER_ENDPOINT + "/health")
    .then(() => {
      originalConsole.info("[DebugLogging] API is healthy, completing initialization");
      interceptConsoleMethods();
      setupGlobalErrorHandler();
      setupUnhandledRejectionHandler();
      initialized = true;
      addLogEntry("info", "[DebugLogging] Initialized");
    })
    .catch((err) => {
      originalConsole.error(
        "[DebugLogging] API is unhealthy, not initializing. Call window.debugService.initialize() to retry:",
        err
      );
    });

  window.debugService = { initialize, sendReport, getLogs: () => [...logBuffer] };
}

function getLogs() {
  return [...logBuffer];
}

function isInitialized() {
  return initialized;
}

const debugLoggingService = {
  initialize,
  sendReport,
  getLogs,
  isInitialized,
};

export default debugLoggingService;
